// Shared shell for every /admin/* page (except login/forgot/reset).
// Sidebar navigation, top bar with account menu, and outlet for the page body.
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
    LayoutDashboard, CalendarDays, BedDouble, Users, Image as ImgIcon,
    ClipboardList, Settings, LogOut, Shield, Menu, X, BarChart3, FileText, Sparkles, ShieldCheck
} from "lucide-react";
import { useState } from "react";
import { useAdminAuth } from "@/lib/adminAuth";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, roles: null, end: true },
    { to: "/admin/reservations", label: "Reservations", icon: ClipboardList, roles: ["reservations_manager"] },
    { to: "/admin/calendar", label: "Calendar", icon: CalendarDays, roles: ["reservations_manager"] },
    { to: "/admin/verification", label: "Verification", icon: ShieldCheck, roles: ["reservations_manager"] },
    { to: "/admin/rooms", label: "Rooms", icon: BedDouble, roles: ["content_manager"] },
    { to: "/admin/media", label: "Media", icon: ImgIcon, roles: ["content_manager"] },
    { to: "/admin/guests", label: "Guests", icon: Users, roles: ["reservations_manager"] },
    { to: "/admin/loyalty", label: "Loyalty", icon: Sparkles, roles: ["reservations_manager"] },
    { to: "/admin/reports", label: "Reports", icon: BarChart3, roles: ["reservations_manager"] },
    { to: "/admin/audit-log", label: "Audit log", icon: FileText, roles: ["super_admin"] },
    { to: "/admin/staff", label: "Staff", icon: Shield, roles: ["super_admin"] },
    { to: "/admin/settings", label: "Settings", icon: Settings, roles: ["super_admin"] },
];

export default function AdminLayout() {
    const { user, logout } = useAdminAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    // First-login gate: force password change before anything else
    if (user?.must_change_password) return <Navigate to="/admin/change-password" replace />;

    const canSee = (roles) => {
        if (!roles) return true;
        if (user?.role === "super_admin") return true;
        return roles.includes(user?.role);
    };

    const doLogout = async () => {
        await logout();
        navigate("/admin/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#f8f4ea] text-[#0b1f22]">
            {/* Sidebar (desktop) */}
            <aside className="hidden lg:flex fixed top-0 left-0 bottom-0 w-64 bg-[#0b1f22] text-ivory flex-col z-20">
                <div className="px-6 py-6 border-b border-white/10">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-gold">Serenity Cove</div>
                    <div className="font-display text-2xl mt-1 leading-tight">Admin</div>
                </div>
                <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
                    {NAV.filter((n) => canSee(n.roles)).map((n) => (
                        <NavLink
                            key={n.to} to={n.to} end={n.end}
                            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-gold/15 text-gold" : "text-ivory/70 hover:bg-white/5 hover:text-ivory"}`}
                            data-testid={`admin-nav-${n.to.replace("/admin/", "").replace("/admin", "dashboard") || "dashboard"}`}
                        >
                            <n.icon size={16} /> {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="px-4 pt-4 pb-6 border-t border-white/10">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-ivory/40">Signed in as</div>
                    <div className="mt-1 text-sm">{user?.name}</div>
                    <div className="text-[11px] text-gold/80 mt-0.5 capitalize">{user?.role?.replace("_", " ")}</div>
                    <button onClick={doLogout} data-testid="admin-logout" className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ivory/60 hover:text-gold">
                        <LogOut size={13} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <header className="lg:hidden sticky top-0 z-30 bg-[#0b1f22] text-ivory flex items-center justify-between px-4 py-3 border-b border-white/10">
                <button onClick={() => setMobileOpen((v) => !v)} className="p-2 -ml-2" data-testid="admin-mobile-menu">
                    {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Serenity Cove · Admin</div>
                <div className="w-6" />
            </header>
            {mobileOpen && (
                <nav className="lg:hidden bg-[#0b1f22] text-ivory px-4 pb-6 space-y-1 border-b border-white/10 sticky top-[52px] z-30">
                    {NAV.filter((n) => canSee(n.roles)).map((n) => (
                        <NavLink
                            key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
                            className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-lg text-sm ${isActive ? "bg-gold/15 text-gold" : "text-ivory/70"}`}
                        >
                            <n.icon size={16} /> {n.label}
                        </NavLink>
                    ))}
                    <button onClick={doLogout} className="mt-2 w-full text-left px-3 py-3 rounded-lg text-sm text-ivory/60 inline-flex items-center gap-3">
                        <LogOut size={16} /> Sign out
                    </button>
                </nav>
            )}

            <main className="lg:pl-64 min-h-screen">
                <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
